import { Component, Input, OnDestroy, OnInit, signal } from '@angular/core';
import { PeriforOnChangeMessages, SignalRService } from 'src/app/shared/services/signal-r.service';
import { StressResultsService } from '../../services/stress-results.service';
import { Observable, catchError, forkJoin, map } from 'rxjs';
import { PackerService } from 'src/app/wellbore-inputs/services/packers.service';
import { SelectItem } from 'primeng/api';
import { UserUnitsModel } from 'src/app/core/components/user-units/user-units.model';
import { StorageKeys, StoreService } from 'src/app/core/services/store.service';
import { unitsLib } from 'src/app/core/services/unit-library';
import { Store } from '@ngneat/elf';
import { ExpansionJointFocus, PackerResultsUi, Packers } from 'src/app/wellbore-inputs/models/packers.model';
import { withUIEntities } from '@ngneat/elf-entities';
import { XyLinePlotUi } from 'src/app/core/models/xy-line-plot.model';
import { PackerLoadDto } from '../../models/packer-load-results.model';

@Component({
    selector: 'app-packer-table-results',
    templateUrl: './packer-table-results.component.html',
    standalone: false
})
export class PackerTableResultsComponent implements OnInit, OnDestroy {
  private _packerCount: number;
  private _forceUnit: string;
  private _pressureUnit: string;
  
  public longLengthUnit: string;
  public isDisplacementSelected = false;
  public resultTypes: any[] = [];
  public columnDefs :{ header: string, field: string }[];
  public resultsDisplay: SelectItem[];
  public displacementTypes: { label: string, value: string }[];
  public resultData: any;
  public results: any;
  public needToCalculate: boolean;
  public isLoading = signal<boolean>(true);
  public packersMissing: boolean;
  public packers: any[] = [];
  public packerData: any;
  public cols: any[];
  public xAxisTitle = '';
  public yAxisTitle = '';
  public plotName: string;
  public downloadPlotName = 'packer_results_plot';
  public shearPinRating: number;
  public upwardMovementLimit: number;
  public downwardMovementLimit: number;
  public annotations: any[] = [];
  public shapes: any[] = [];
  public displayResults: any[] = [];

  
  public showExpansionJointFocusToggle = false;
  public expansionJointFocus: ExpansionJointFocus = ExpansionJointFocus.PackerBody;
  public expansionJointFocusOptions = [
    { label: 'Packer Body', value: ExpansionJointFocus.PackerBody },
    { label: 'Exp. Joint', value: ExpansionJointFocus.ExpansionJoint }
  ];

  public plot = {
    data: [],
    layout: { },
    config: {}
  };

    // State Management
    private _componentId: string;
    @Input() set componentId(value: string) {
      this._componentId = value;
      this.packerResultsStore = this._storeService.createStore(this.componentId, new PackerResultsUi, withUIEntities<XyLinePlotUi>());
      this.selectedResultDisplay = this.packerResultsStore.state.selectedResultDisplay || 'grid';
      this.selectedPacker = this.packerResultsStore.state.selectedPacker || null;
      this.selectedResultView = this.packerResultsStore.state.selectedResultView || 'differentialPressure';
      this.displacementType = this.packerResultsStore.state.displacementType || 'relativeDisplacements';
    }
    get componentId(): string {
      return this._componentId;
    }
    public packerResultsStore: Store;

    public selectedResultDisplay: string = 'grid';
    public selectedPacker: any = null;
    public selectedResultView: string = 'differentialPressure';
    public displacementType: string = 'relativeDisplacements';

    public displacementTooltip = "Absolute Displacement refers to the actual stretch of the tubular string relative to its nominal length before running.  It essentially performs a stretch calculation on the string include gravity loads and other effects (thermal, ballooning, Hooke’s Law, etc.)  Any change in temperature, pressure, etc. will change the amount of stretch in the string.  If you wanted to theoretically calculate the amount of physical stretch based on first principles, this result would be given by Absolute Displacement. \n \n Relative Displacement assumes the Absolute Displacement reported for the Initial Conditions (including the setting of all packers and any Slackoff/Pickup) as the point of reference and then reports the DIFFERENTIAL STRAIN for all subsequent loads relative to that Initial Condition frame of reference.  Since the Packer Depth is fixed after setting, its position in the well does not change. And so Relative Displacement correct reports the localized movement or stroke of say moving seals in a PBR or receptacle."

  constructor(
    private _storeService : StoreService,
    private _signalRService: SignalRService,
    private _stressResultsService: StressResultsService,
    private _packerService: PackerService
  ) { }

  async ngOnInit(): Promise<void> {
    const uu = await this._storeService.get<UserUnitsModel>(StorageKeys.UNITS);
    this._forceUnit = uu.force;
    this.longLengthUnit = uu.longLengths;
    this._pressureUnit = unitsLib[uu.pressure].symbol;

    const lengthDecimal = this.longLengthUnit == 'ft' ? 2 : 3;

    this.cols = [
      {
        field: 'component',
        header: 'Component',
        valueFormatter: (params: any) => params
      },
      {
        field: 'depth',
        header: `Depth (${this.longLengthUnit})`,
        valueFormatter: (params: any) => params ? this.formatDecimalNew(params, 2) : '-'
      },
      {
        field: 'loadCaseName',
        header: 'Load',
        valueFormatter: (params: any) => params
      },
      {
        field: 'differentialPressure',
        header: `Differential Pressure (${this._pressureUnit})`,
        valueFormatter: (params: any) => this.formatDecimalNew(params, 2)
      },
      {
        field: 'tubingToPackerForce',
        header: `Tubing to Packer Force * (${this._forceUnit})`,
        valueFormatter: (params: any) => this.formatDecimalNew(params, 2)
      },
      {
        field: 'latchShearLoad',
        header: `Latch Shear Load (${this._forceUnit})`,
        valueFormatter: (params: any) => this.formatDecimalNew(params, 2)
      },
      {
        field: 'packerToCasingForce',
        header: `Packer to Casing Force * (${this._forceUnit})`,
        valueFormatter: (params: any) => this.formatDecimalNew(params, 2)
      },
      {
        field: 'axialForceAbovePacker',
        header: `Axial Force Above Packer (${this._forceUnit})`,
        valueFormatter: (params: any) => this.formatDecimalNew(params, 2)
      },
      {
        field: 'axialForceOnTailpipe',
        header: `Axial Force On Tailpipe (${this._forceUnit})`,
        valueFormatter: (params: any) => this.formatDecimalNew(params, 2)
      },
      {
        field: 'total',
        header: `Total Displacement (${this.longLengthUnit})`,
        valueFormatter: (params: any) => this.formatDecimalNew(params, lengthDecimal)
      },
      {
        field: 'thermal',
        header: `Thermal Displacement (${this.longLengthUnit})`,
        valueFormatter: (params: any) => this.formatDecimalNew(params, lengthDecimal)
      },
      {
        field: 'poisson',
        header: `Poisson Displacement (${this.longLengthUnit})`,
        valueFormatter: (params: any) => this.formatDecimalNew(params, lengthDecimal)
      },
      {
        field: 'hooke',
        header: `Hooke Displacement (${this.longLengthUnit})`,
        valueFormatter: (params: any) => this.formatDecimalNew(params, lengthDecimal)
      },
      {
        field: 'buckling',
        header: `Buckling Displacement (${this.longLengthUnit})`,
        valueFormatter: (params: any) => this.formatDecimalNew(params, lengthDecimal)
      }
    ];

    this.displacementTypes = [
      { label: "Relative Displacement", value: "relativeDisplacements" },
      { label: "Absolute Displacement", value: "absoluteDisplacements" }
    ];

    this.resultsDisplay = [
      { label: 'Grid', value: 'grid' },
      { label: 'Plot', value: 'plot' }
    ];
    
    const hub = this._signalRService.getConnectionToNotificationHub();
    this._signalRService.subscribeToEventFilteredByDesignId(hub, SignalRService.ON_PFB_CHANGE, d => this.signalRfunc(d));
    
    this.refreshResults();
  }

  signalRfunc(data: any) {
    if (data.action == PeriforOnChangeMessages.REFRESH_RESULTS || data.action == PeriforOnChangeMessages.REFRESH_PACKERS ||
      data.action == PeriforOnChangeMessages.REFRESH_STRING_INPUTS) {
      this.refreshResults();
    }
  }

  public refreshResults() {
    this.isLoading.set(true);
    this.results = [];
    this.plot.data = [];
    const sources = [
      this._stressResultsService.getPackerLoads() as Observable<any>,
      this._packerService.getPackersForTubular() as Observable<Packers>
    ];

    forkJoin(sources).pipe(
      map(([results, packers]) => {
        this.resultData = results;
        this._packerCount = packers.packers.length;

        this.packerData = packers.packers;
        this.packers = this.resultData?.map(x => x.packerName ?? 'Packer') ?? [];
        if (!this.selectedPacker) {
          this.selectedPacker = this.packers[0];
          this.packerResultsStore.update(state => ({...state, selectedPacker: this.packers[0]}));
        }
        this.plotName = 'packerResultsPlot - ' + this.selectedPacker + ' ' + this.selectedResultView + ' ' + this.displacementType;
        this.handleErrors();
        this.displacementType = this.displacementTypes[0].value;

        const rawResults = this.resultData[0]?.packerLoadResults ?? [];
        this.results = this.transformPackerLoadResults(rawResults);

        const packerDepth = this.resultData[0]?.packerMd;
        this.results.forEach(row => {
          if (row.component === 'Packer' || row.component === 'Packer Body') {
            row.depth = packerDepth;
          }
        });

        this.updateLatchLoadColumn();

        this.showResults();
        this.selectPacker(this.selectedPacker);
      }),
      catchError(err => {
        this.isLoading.set(false);
        return err;
      })).subscribe();
  }

  public toggleGridPlot(e): void {
    if (e.value === 'plot' || e.value === 'grid') {
      this.selectedResultDisplay = e.value;
    }
    this.packerResultsStore.update(state => ({...state, selectedResultDisplay: e.value}));
    this.plotName = 'packerResultsPlot - ' + this.selectedPacker + ' ' + this.selectedResultView + ' ' + this.displacementType;
    this.showResults();
  }

  public displacementTypeChange(event: any) {
    this.displacementType = event.value;
    this.packerResultsStore.update(state => ({...state, displacementType: event.value}));
    this.plotName = 'packerResultsPlot - ' + this.selectedPacker + ' ' + this.selectedResultView + ' ' + this.displacementType;
    this.showResults();
  }

  public selectPacker(packer: any) {
    const selectedPacker = packer?.value ?? packer?.originalEvent?.srcElement?.innerText ?? packer;
    this.selectedPacker = selectedPacker;
    this.packerResultsStore.update(state => ({...state, selectedPacker: selectedPacker}));

    const selectedPackerData = this.packerData.find(x => x.name == selectedPacker);
    this.showExpansionJointFocusToggle =
      selectedPackerData?.hasExpansionJoint === true &&
      selectedPackerData?.advancedExpansionJoint?.isDepthNotAtPacker === true;

    const selectedIndex = this.packers.indexOf(selectedPacker);
    const safeIndex = selectedIndex === -1 ? 0 : selectedIndex;
    const rawResults = this.resultData[safeIndex]?.packerLoadResults ?? [];

    this.results = this.transformPackerLoadResults(rawResults);

    const packerDepth = this.resultData[safeIndex]?.packerMd;
    this.results.forEach(row => {
      if (row.component === 'Packer' || row.component === 'Packer Body') {
        row.depth = packerDepth;
      }
    });

    this.plotName = 'packerResultsPlot - ' + this.selectedPacker + ' ' + this.selectedResultView + ' ' + this.displacementType;

    this.updateLatchLoadColumn();

    this.handleErrors();

    this.showResults();

    this.setAnnotations();

    this.isLoading.set(false);
  }

  private transformPackerLoadResults(packerLoadResults: PackerLoadDto[]): any[] {
    const transformedResults: any[] = [];

    packerLoadResults.forEach(load => {
      if (load.expansionJointForces) {
        transformedResults.push({
          component: 'Exp. Joint',
          depth: load.expansionJointForces.depth,
          loadCaseName: load.loadCaseName,
          differentialPressure: load.expansionJointForces.differentialPressure,
          tubingToPackerForce: load.expansionJointForces.tubingToExpansionJointForce,
          axialForceAbovePacker: load.expansionJointForces.axialForceAboveExpansionJoint,
          axialForceOnTailpipe: load.expansionJointForces.axialForceBelowExpansionJoint,
          latchShearLoad: null,
          packerToCasingForce: null,
          absoluteDisplacements: load.expansionJointForces.absoluteDisplacements,
          relativeDisplacements: load.expansionJointForces.relativeDisplacements
        });
      }

      transformedResults.push({
        component: load.expansionJointForces ? 'Packer Body' : 'Packer',
        depth: null,
        loadCaseName: load.loadCaseName,
        differentialPressure: load.differentialPressure,
        tubingToPackerForce: load.tubingToPackerForce,
        axialForceAbovePacker: load.axialForceAbovePacker,
        axialForceOnTailpipe: load.axialForceOnTailpipe,
        latchShearLoad: load.latchShearLoad,
        packerToCasingForce: load.packerToCasingForce,
        absoluteDisplacements: load.absoluteDisplacements,
        relativeDisplacements: load.relativeDisplacements
      });
    });

    return transformedResults;
  }

  private updateLatchLoadColumn(): void {
    const selectedPackerData = this.packerData.find(x => x.name == this.selectedPacker);
    this.results = this.results.map(r =>
      selectedPackerData && (!selectedPackerData.hasExpansionJoint || !selectedPackerData.expansionJoint?.isSheared) ? { ...r, latchShearLoad: 'N/A' } : { ...r }
    );
  }
  
  private setAnnotations() {
    const selectedPacker = this.packerData.find(x => x.name == this.selectedPacker);
    const expansionJoint = selectedPacker?.advancedExpansionJoint;
    const isSheared = expansionJoint?.isSheared ?? false;
    this.shearPinRating = !isSheared ? expansionJoint?.notShearedState?.shearPinRating : null;

    const movementState = expansionJoint?.movementState;
    this.upwardMovementLimit = this.displacementType == 'relativeDisplacements' && isSheared ? (movementState?.spaceOut - movementState?.totalStrokeLength) : null;
    this.downwardMovementLimit = this.displacementType == 'relativeDisplacements' && isSheared ? movementState?.spaceOut : null;

    this.annotations = [];
    this.shapes = [];
    const selectedView = this.selectedResultView;

    if (this.selectedResultDisplay === 'plot' && (!selectedPacker.hasExpansionJoint || (selectedView == 'latchShearLoad' && this.shearPinRating == null) || (selectedView == 'total' && this.upwardMovementLimit == null) ||
      (selectedView != 'total' && selectedView != 'latchShearLoad'))) {
      this.upwardMovementLimit = null;
      this.downwardMovementLimit = null;
      this.shearPinRating = null;
      this.annotations = [];
      this.shapes = [];
      return;
    }

    let annotationLabel = '';
    let unit = '';
    let value = 0;
    switch (selectedView) {
      case 'latchShearLoad':
        annotationLabel = 'Shear Pin Rating: ';
        unit = this._forceUnit;
        value = this.shearPinRating;
        break;
      case 'total':
        annotationLabel = 'Upward Limit: ';
        unit = this.longLengthUnit;
        value = this.upwardMovementLimit;
        break;
    }

    this.shapes = [
      {
        type: "line",
        xref: "paper",
        x0: 0,
        y0: value,
        x1: 1,
        y1: value,
        line: {
          color: "rgb(239, 83, 80)",
          width: 2,
          dash: "dot",
        },
      },
    ];

    this.annotations = [
      {
        showarrow: false,
        text: `${annotationLabel}` +  value + ` ${unit}`,
        font: {
          color: "rgb(239, 83, 80)"
        },
        xref: "paper",
        align: "right",
        x: 1,
        xanchor: "right",
        y: value,
        yanchor: "bottom",
      },
    ];

    if (selectedView == 'total') {
      this.shapes.push(
        {
          type: "line",
          xref: "paper",
          x0: 0,
          y0: this.downwardMovementLimit,
          x1: 1,
          y1: this.downwardMovementLimit,
          line: {
            color: "rgb(239, 83, 80)",
            width: 2,
            dash: "dot",
          },
        }
      );
      this.annotations.push(
        {
          showarrow: false,
          text: `Downward Limit: ` + this.downwardMovementLimit + ` ${this.longLengthUnit}`,
          font: {
            color: "rgb(239, 83, 80)"
          },
          xref: "paper",
          align: "right",
          x: 1,
          xanchor: "right",
          y: this.downwardMovementLimit,
          yanchor: "bottom",
        }
      );
    }
  }

  public showResults(): void {
    this.isDisplacementSelected = this.resultTypes.find(x => x.value == this.selectedResultView)?.isDisplacement ?? false;
    if(this.selectedResultDisplay === 'grid') {
      this.handleDisplacementResults();
    } else {
      this.handleMultipleLoadResults();
    }
    this.setAnnotations();
  }

  private handleDisplacementResults(): void {
    const plotExpJoint = this.expansionJointFocus === ExpansionJointFocus.ExpansionJoint;
    const filteredResults = this.showExpansionJointFocusToggle
      ? this.results.filter(r => plotExpJoint ? r.component === 'Exp. Joint' : r.component !== 'Exp. Joint')
      : this.results;

    this.displayResults = filteredResults.map(element => {
      const displacement = element[this.displacementType];
      return {
        ...element,
        total: displacement?.total ?? null,
        thermal: displacement?.thermal ?? null,
        poisson: displacement?.poisson ?? null,
        hooke: displacement?.hooke ?? null,
        buckling: displacement?.buckling ?? null,
      };
    });
  }

  private handleErrors() {
    if (this._packerCount == 0) {
      this.results = [];
      this.packers = [];
      this.packersMissing = true;
      this.needToCalculate = false;
      this.isLoading.set(false);
      return;
    } else if ((this.resultData?.length ?? 0) < 1 && this._packerCount > 0) {
      this.results = [];
      this.packersMissing = false;
      this.needToCalculate = true;
      this.isLoading.set(false);
      return;
    } else {
      this.packersMissing = false;
      this.needToCalculate = false;
      this.isLoading.set(false);
    }
  }

  public toggleResultsView(e): void {
    this.selectedResultView = e.value;
    this.packerResultsStore.update(state => ({...state, selectedResultView: e.value}));
    this.isDisplacementSelected = this.resultTypes.find(x => x.value == this.selectedResultView)?.isDisplacement ?? false;
    this.plotName = 'packerResultsPlot - ' + this.selectedPacker + ' ' + this.selectedResultView + ' ' + this.displacementType;
    this.handleMultipleLoadResults();
    this.setAnnotations();
  }

  private handleMultipleLoadResults() {
    this.columnDefs = [];

    this.resultTypes = [
      { label: `Differential Pressure (${this._pressureUnit})`, value: "differentialPressure", isDisplacement: false },
      { label: `Tubing to Packer Force (${this._forceUnit})`, value: "tubingToPackerForce", isDisplacement: false },
      { label: `Axial Force Above Packer (${this._forceUnit})`, value: "axialForceAbovePacker", isDisplacement: false },
      { label: `Axial Force On Tailpipe (${this._forceUnit})`, value: "axialForceOnTailpipe", isDisplacement: false },
      { label: `Latch Shear Load (${this._forceUnit})`, value: "latchShearLoad", isDisplacement: false },
      { label: `Packer to Casing Force (${this._forceUnit})`, value: "packerToCasingForce", isDisplacement: false },

      { label: `Total Displacement (${this.longLengthUnit})`, value: "total", isDisplacement: true },
      { label: `Thermal Displacement (${this.longLengthUnit})`, value: "thermal", isDisplacement: true },
      { label: `Poisson Displacement (${this.longLengthUnit})`, value: "poisson", isDisplacement: true },
      { label: `Hooke Displacement (${this.longLengthUnit})`, value: "hooke", isDisplacement: true },
      { label: `Buckling Displacement (${this.longLengthUnit})`, value: "buckling", isDisplacement: true }
    ];

    const ejRows = this.results.filter(r => r.component === 'Exp. Joint');
    const packerRows = this.results.filter(r => r.component !== 'Exp. Joint');
    const plotExpJoint = this.expansionJointFocus === ExpansionJointFocus.ExpansionJoint;

    const getValue = (row: any) =>
      this.isDisplacementSelected
        ? row[this.displacementType]?.[this.selectedResultView]
        : row[this.selectedResultView];

    const packerTrace = {
      name: plotExpJoint ? 'Packer Body' : 'Packer',
      x: packerRows.map(r => r.loadCaseName),
      y: packerRows.map(r => getValue(r)),
      type: 'bar',
      width: 0.55,
    };

    const expJointTrace = {
      name: 'Exp. Joint',
      x: ejRows.map(r => r.loadCaseName),
      y: ejRows.map(r => getValue(r)),
      type: 'bar',
      width: 0.55,
    };

    this.yAxisTitle = this.resultTypes.find(x => x.value === this.selectedResultView).label;

    this.plot.data = plotExpJoint ? [expJointTrace] : [packerTrace];
    this.isLoading.set(false);
  }

  private formatDecimalNew(value: any, numDecimalPoints: number): string {
    return !isNaN(value) ? (parseFloat(value).toFixed(numDecimalPoints)) : value;
  }

  ngOnDestroy() {
    this.signalRfunc = null;
  }
}
